import { useContext, useState } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { deleteUser } from 'firebase/auth';
import { logEvent } from 'firebase/analytics';
import type { User } from '@/models';
import { errorToastOptions, getAuthErrorMessage, successToastOptions } from '@/helpers';
import { isDefinitelyOffensiveName } from '@/helpers/moderation/nameModeration';
import { callUpdateUserName } from '@/helpers/moderation/updateUserName';
import type { FirebaseError } from 'firebase/app';
import { ConfirmModal, StringEntryModal, ReauthModal, SetPasswordModal, ChangePasswordModal } from '../modals';
import { FirebaseContext } from '@/session/FirebaseContext';

export function useAccountSettingsDisclosures() {
  const editNameDisclosure = useDisclosure();
  const deleteAccountDisclosure = useDisclosure();
  const setPasswordDisclosure = useDisclosure();
  const changePasswordDisclosure = useDisclosure();
  const reauthDisclosure = useDisclosure();
  return {
    editNameDisclosure,
    deleteAccountDisclosure,
    setPasswordDisclosure,
    changePasswordDisclosure,
    reauthDisclosure,
  };
}

export function AccountSettingsModals(props: {
  userData: User;
  editNameDisclosure: ReturnType<typeof useDisclosure>;
  deleteAccountDisclosure: ReturnType<typeof useDisclosure>;
  setPasswordDisclosure: ReturnType<typeof useDisclosure>;
  changePasswordDisclosure: ReturnType<typeof useDisclosure>;
  reauthDisclosure: ReturnType<typeof useDisclosure>;
}) {
  const { name } = props.userData;
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingSetPassword, setPendingSetPassword] = useState(false);

  async function performDelete() {
    try {
      await deleteUser(ctx.user!);
      logEvent(ctx.analytics, 'delete_account');
      toast(successToastOptions('Account Successfully Deleted', 'We hope you enjoyed your time playing 10 Teams!'));
    } catch (err) {
      const firebaseErr = err as FirebaseError;
      if (firebaseErr.code === 'auth/requires-recent-login') {
        setPendingDelete(true);
        props.reauthDisclosure.onOpen();
      } else {
        toast(errorToastOptions('Error Deleting Account', getAuthErrorMessage(firebaseErr)));
      }
    }
  }

  async function handleReauthSuccess() {
    if (pendingDelete) {
      setPendingDelete(false);
      await performDelete();
    }
    if (pendingSetPassword) {
      setPendingSetPassword(false);
      props.setPasswordDisclosure.onOpen();
    }
  }

  function handleDeleteConfirm() {
    props.deleteAccountDisclosure.onClose();
    void performDelete();
  }

  return (
    <>
      <StringEntryModal
        isOpen={props.editNameDisclosure.isOpen}
        title='Edit Name'
        label='Your Name'
        onClose={props.editNameDisclosure.onClose}
        onSubmit={async (newName) => {
          const trimmed = newName.trim();
          if (isDefinitelyOffensiveName(trimmed)) {
            toast(
              errorToastOptions(
                'Name not allowed',
                'That name is not allowed. Please choose another.',
              ),
            );
            throw new Error('name-not-allowed');
          }
          const result = await callUpdateUserName(ctx.funcs, trimmed);
          if (!result.accepted) {
            toast(
              errorToastOptions(
                'Name not allowed',
                'That name is not allowed. Please choose another.',
              ),
            );
            throw new Error('name-policy-violation');
          }
        }}
        getInitialValue={() => name}
      />
      <ConfirmModal
        isOpen={props.deleteAccountDisclosure.isOpen}
        title='Delete Account'
        message={"Are you sure that you'd like to delete your account?"}
        onClose={props.deleteAccountDisclosure.onClose}
        isDestructive
        onConfirm={handleDeleteConfirm}
      />
      <SetPasswordModal
        isOpen={props.setPasswordDisclosure.isOpen}
        onClose={props.setPasswordDisclosure.onClose}
        onNeedsReauth={() => {
          setPendingSetPassword(true);
          props.reauthDisclosure.onOpen();
        }}
      />
      <ChangePasswordModal
        isOpen={props.changePasswordDisclosure.isOpen}
        onClose={props.changePasswordDisclosure.onClose}
      />
      <ReauthModal
        isOpen={props.reauthDisclosure.isOpen}
        onClose={() => {
          setPendingDelete(false);
          setPendingSetPassword(false);
          props.reauthDisclosure.onClose();
        }}
        onSuccess={handleReauthSuccess}
        title={pendingDelete ? 'Confirm before deleting account' : 'Confirm your identity'}
      />
    </>
  );
}
